import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Sun,
  Columns3,
  Building2,
  Palette,
  Menu,
  Phone,
  CalendarDays,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { useState, Suspense } from 'react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { NavigationDrawer, LoadingSpinner } from '@elka/ui';
import { Sidebar, TopBar, type SidebarNavGroup } from '@elka/layout';
import { useAuth } from '../features/auth/hooks/useAuth';
import { useTheme } from '@elka/theme';
import { useMyTickets } from '../features/tickets/hooks/useMyTickets';
import { NotificationDropdown } from '../features/notifications/components/NotificationDropdown';
import { CrmActionsProvider } from '../features/shell/CrmActionsProvider';
import { useCrmActions } from '../features/shell/useCrmActions';
import { PageMetaProvider } from '../features/shell/PageMeta';
import { useCurrentPageMeta } from '../features/shell/usePageMeta';
import { localDateStr } from '../lib/relativeTime';

type NavKey = 'my-dashboard' | 'pipeline' | 'customers';

// The three nav items, each with its top-bar title/subtitle (detail pages override via
// usePageMeta). My Day's subtitle is today's date, filled in at render.
const NAV: { key: NavKey; label: string; icon: LucideIcon; subtitle?: string }[] = [
  { key: 'my-dashboard', label: 'היום שלי', icon: Sun },
  { key: 'pipeline', label: 'צנרת', icon: Columns3, subtitle: 'גרור עסקה בין שלבים' },
  { key: 'customers', label: 'לקוחות', icon: Building2, subtitle: 'חברות ואנשי קשר במקום אחד' },
];

// Flat list reused by the mobile NavigationDrawer (which takes {to,label,icon}).
const MOBILE_NAV = NAV.map(({ key, label, icon }) => ({ to: `/${key}`, label, icon }));

// Boards use the full width; everything else is capped.
const FULL_WIDTH_ROUTES = new Set(['pipeline']);

const PILL_BUTTON =
  'inline-flex h-9 items-center gap-1.5 rounded-full text-[13px] transition-colors';
const PILL_SECONDARY = `${PILL_BUTTON} text-ink-2 hover:bg-page px-3 font-semibold`;

export function Layout() {
  return (
    <CrmActionsProvider>
      <PageMetaProvider>
        <LayoutShell />
      </PageMetaProvider>
    </CrmActionsProvider>
  );
}

function LayoutShell() {
  const { signOut, user } = useAuth();
  const { tickets: myOpenTickets } = useMyTickets();
  const { openLog, openNewContact } = useCrmActions();
  const pageMeta = useCurrentPageMeta();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // The first path segment; detail routes stay highlighted under their list item.
  const segment = location.pathname.split('/')[1] || 'my-dashboard';
  const activeKey = segment === 'deals' ? 'pipeline' : segment;
  const navItem = NAV.find((n) => n.key === activeKey);
  const title = pageMeta?.title ?? navItem?.label ?? 'CRM אלקה';
  const subtitle = pageMeta
    ? pageMeta.subtitle
    : activeKey === 'my-dashboard'
      ? format(new Date(), 'EEEE, d בMMMM', { locale: he })
      : navItem?.subtitle;

  const today = localDateStr();
  const myDueCount = myOpenTickets.filter((t) => t.dueDate && t.dueDate <= today).length;
  const badges: Partial<Record<NavKey, number>> = {
    'my-dashboard': myDueCount || undefined,
  };

  const displayName = user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || '';
  const userInitial = displayName.charAt(0).toUpperCase();

  const sidebarGroups: SidebarNavGroup[] = [
    {
      items: NAV.map(({ key, label, icon: Icon }) => ({
        key,
        label,
        icon: <Icon size={18} />,
        href: `/${key}`,
        badge: badges[key],
      })),
    },
  ];

  return (
    <div className="bg-page flex min-h-screen">
      {/* Desktop sidebar (RTL: right side via border-l + flex order) */}
      <div className="hidden md:block">
        <Sidebar
          appName="אלקה CRM"
          tagline="צוות מכירות"
          width={200}
          groups={sidebarGroups}
          activeKey={activeKey}
          onNavigate={(item) => item.href && navigate(item.href)}
          footer={
            <>
              <div className="bg-brand flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold text-white">
                {userInitial}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-ink truncate text-xs font-bold">{displayName}</div>
                <button onClick={signOut} className="text-muted text-[10px] hover:underline">
                  התנתק
                </button>
              </div>
              <button
                onClick={toggleTheme}
                title={theme === 'elkaBlue' ? 'מצב זהב' : 'מצב כחול'}
                aria-label={theme === 'elkaBlue' ? 'מצב זהב' : 'מצב כחול'}
                className="text-muted hover:text-ink rounded-control flex h-10 w-10 shrink-0 items-center justify-center transition-[color,transform] active:scale-[0.96]"
              >
                <Palette size={16} />
              </button>
            </>
          }
        />
      </div>

      {/* Mobile drawer */}
      <NavigationDrawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        items={MOBILE_NAV}
        user={{ name: displayName, initial: userInitial, photoURL: user?.photoURL }}
        onSignOut={signOut}
        footerContent={
          <button
            onClick={toggleTheme}
            className="text-muted hover:text-ink flex w-full items-center justify-center gap-2 py-2 text-xs transition-colors"
          >
            <Palette size={16} />
            {theme === 'elkaBlue' ? 'מצב זהב' : 'מצב כחול'}
          </button>
        }
      />

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          dense
          title={title}
          subtitle={subtitle}
          actions={
            <>
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="border-line text-ink-2 rounded-control relative flex h-8 w-8 items-center justify-center border transition-transform after:absolute after:inset-[-4px] after:content-[''] active:scale-[0.96] md:hidden"
                aria-label="תפריט"
              >
                <Menu size={18} />
              </button>
              <NotificationDropdown />
            </>
          }
        />

        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <div className={FULL_WIDTH_ROUTES.has(segment) ? 'w-full' : 'mx-auto max-w-7xl'}>
            {/* One Suspense boundary for all lazy route pages — keeps the nav/header/footer
                chrome mounted and shows the fallback only in the content area. */}
            <Suspense
              fallback={
                <div className="flex min-h-[60vh] items-center justify-center">
                  <LoadingSpinner size="lg" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      {/* Global quick-action pill (bottom-start, RTL) */}
      <div className="bg-surface fixed bottom-5 left-5 z-40 flex items-center gap-1 rounded-full p-1.5 shadow-[0_1px_2px_rgba(0,0,0,0.06),var(--shadow-overlay)]">
        <button
          type="button"
          onClick={() => openLog('call')}
          className={`${PILL_BUTTON} bg-brand hover:bg-brand-ink px-3.5 font-bold text-white`}
        >
          <Phone size={15} aria-hidden="true" />
          רשום שיחה
        </button>
        <button type="button" onClick={() => openLog('meeting')} className={PILL_SECONDARY}>
          <CalendarDays size={15} aria-hidden="true" />
          פגישה
        </button>
        <button type="button" onClick={() => openNewContact()} className={PILL_SECONDARY}>
          <UserPlus size={15} aria-hidden="true" />
          איש קשר
        </button>
      </div>
    </div>
  );
}
